import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, downloadFile } from '@/lib/api/client'

/** One reason a row in an imported spreadsheet was rejected. */
export interface ImportRowProblem {
  /** The row number the spreadsheet itself shows — headings are row 1. Zero means the file. */
  row: number
  /** The heading, so it can be pointed at in the file. Null when it is the whole row. */
  column: string | null
  message: string
}

/** What one accepted row did, for an import that can do more than one thing with a row. */
export interface ImportRowOutcome {
  row: number
  /** "created", "updated", "updated; new department 'Logistics'". */
  outcome: string
}

export interface ImportReport {
  applied: boolean
  rowsRead: number
  accepted: number
  problems: ImportRowProblem[]
  /** Headings the template does not have — reported, never silently dropped. */
  unknownColumns: string[]
  /** Per-row results — on a check, what each row would do. Empty for create-only imports. */
  outcomes?: ImportRowOutcome[]
}

/**
 * Where an entity's bulk endpoints live.
 *
 * <p>The server puts all four under one prefix, so a screen names the prefix once instead of
 * four URLs that can drift apart — which is how "Import" and "Download the template" end up
 * pointing at different entities.
 */
export function bulkPaths(prefix: string) {
  return {
    template: `${prefix}/import/template`,
    validate: `${prefix}/import/validate`,
    apply: `${prefix}/import`,
    export: `${prefix}/export`,
  }
}

/**
 * Check a file, then commit it.
 *
 * <p>Two calls on purpose. The first writes nothing and comes back with every row that is
 * wrong; the second refuses the whole file if any row still is. Between them the person
 * holding the spreadsheet gets to fix it, which is the only workflow that does not end in
 * somebody reconciling a half-imported list by hand.
 *
 * @param invalidateKey the query key to refresh once rows have actually been written
 */
export function useBulkImport(
  prefix: string, invalidateKey: readonly unknown[], params?: Record<string, string | boolean>,
) {
  const queryClient = useQueryClient()
  const paths = bulkPaths(prefix)

  const send = (path: string) => async (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return (await apiClient.post<ImportReport>(path, form, { params })).data
  }

  return {
    validate: useMutation({ mutationFn: send(paths.validate) }),
    apply: useMutation({
      mutationFn: send(paths.apply),
      onSuccess: (report) => {
        if (report.applied) {
          void queryClient.invalidateQueries({ queryKey: invalidateKey })
        }
      },
    }),
    downloadTemplate: (fileName: string) => downloadFile(paths.template, fileName),
    downloadExport: (fileName: string, params?: Record<string, unknown>) =>
      downloadFile(paths.export, fileName, params),
  }
}
